import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

export interface ConflictFilesInfo {
  conflicting: File[];
  nonConflicting: File[];
}

export interface FileConflictModalProps {
  isOpen: boolean;
  conflictFiles: ConflictFilesInfo | null;
  isChecking: boolean;
  onCancel: () => void;
  onSkip: () => void;
  onOverwrite: () => void;
}

export function FileConflictModal({
  isOpen,
  conflictFiles,
  isChecking,
  onCancel,
  onSkip,
  onOverwrite,
}: FileConflictModalProps) {
  const { t } = useTranslation();

  if (!conflictFiles) return null;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={open => {
        if (!open) onCancel();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="text-yellow-500" size={20} />
            {t('project.fileNameConflict')}
          </DialogTitle>
          <DialogDescription>
            {conflictFiles.conflicting.length} {t('project.file')}
            {conflictFiles.conflicting.length > 1 ? 's' : ''}{' '}
            {t('project.filesAlreadyExist')}
          </DialogDescription>
        </DialogHeader>
        <div className="px-6 py-4">
          <p className="text-sm text-gray-600 mb-2">
            {t('project.conflictingFiles')}
          </p>
          <ul className="text-sm space-y-1 max-h-48 overflow-y-auto border rounded-md p-3 bg-gray-50">
            {conflictFiles.conflicting.map((file, index) => (
              <li key={index} className="flex items-center gap-2">
                <span className="w-3.5 h-3.5 text-gray-400">📄</span>
                <span className="truncate">{file.name}</span>
              </li>
            ))}
          </ul>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={onCancel}
            className="flex-1"
            disabled={isChecking}
          >
            {t('common.cancel')}
          </Button>
          {conflictFiles.nonConflicting.length > 0 && (
            <Button
              variant="default"
              onClick={onSkip}
              className="flex-1"
              disabled={isChecking}
            >
              {t('project.skipDuplicates')}
            </Button>
          )}
          <Button
            variant="destructive"
            onClick={onOverwrite}
            className="flex-1"
            disabled={isChecking}
          >
            {t('project.overwriteAll')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default FileConflictModal;
